import { type ReactNode, useState } from 'react';

import { cmsPublicContent } from '@shared/cms/content';
import type { SiteCmsDocumentResponse, SiteCmsMenuItem, SiteCmsPageDraft } from '@shared/types/entities';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ChevronRight, Plus, Trash2 } from 'lucide-react';

import {
  cmsChangeMenuType,
  cmsIdentity,
  cmsKeepIdentity,
  cmsMenuCount,
  normalizeCmsMenu,
} from './site-cms-workspace-model';
import { useCmsInteractions } from './use-cms-interactions';

type Props = {
  items: SiteCmsMenuItem[];
  onChange: (items: SiteCmsMenuItem[]) => void;
  depth?: number;
  revision?: number;
  pages: SiteCmsPageDraft[];
  documents: SiteCmsDocumentResponse[];
};
const home = (): SiteCmsMenuItem => ({ type: 'PAGE', label: 'Accueil', slug: 'home', children: [] });
const height = (item: SiteCmsMenuItem): number => 1 + Math.max(0, ...item.children.map(height));
export default function SiteCmsNavigationEditor({ items, onChange, pages, documents, revision = 0 }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ items: SiteCmsMenuItem[]; revision: number } | null>(null);
  const [notice, setNotice] = useState('');
  const { ask } = useCmsInteractions();
  const count = cmsMenuCount(items);
  function find(nodes: SiteCmsMenuItem[], id: string | null): SiteCmsMenuItem | undefined {
    for (const node of nodes) {
      if (cmsIdentity(node) === id) return node;
      const nested = find(node.children, id);
      if (nested) return nested;
    }
  }
  const current = find(items, selected);
  function patch(
    nodes: SiteCmsMenuItem[],
    id: string,
    fn: (entry: SiteCmsMenuItem, siblings: SiteCmsMenuItem[], index: number) => SiteCmsMenuItem[],
  ): SiteCmsMenuItem[] {
    const index = nodes.findIndex(n => cmsIdentity(n) === id);
    if (index >= 0) return fn(nodes[index], nodes, index);
    return nodes.map(item => cmsKeepIdentity(item, { ...item, children: patch(item.children, id, fn) }));
  }
  function commit(next: SiteCmsMenuItem[], message: string, structural = true) {
    if (structural) setUndo({ items, revision });
    else setUndo(null); // Never restore a full menu over subsequent typing.
    onChange(normalizeCmsMenu(next));
    setNotice(message);
  }
  function edit(item: SiteCmsMenuItem, next: SiteCmsMenuItem) {
    commit(
      patch(items, cmsIdentity(item), (_, siblings, index) =>
        siblings.map((old, i) => (i === index ? cmsKeepIdentity(item, next) : old)),
      ),
      '',
      false,
    );
  }
  async function changeType(item: SiteCmsMenuItem, type: SiteCmsMenuItem['type']) {
    if (type === item.type) return;
    if (
      type === 'EXTERNAL_LINK' &&
      item.children.length &&
      (await ask({
        title: 'Conserver les sous-liens ?',
        message: `Les ${item.children.length} sous-liens seront déplacés juste après cette entrée. Aucun lien ne sera supprimé.`,
        choices: [{ value: 'convert', label: 'Convertir et remonter les sous-liens' }],
      })) !== 'convert'
    )
      return;
    if (type === 'GROUP' && !item.children.length && count >= 60) return;
    commit(
      patch(items, cmsIdentity(item), (_, siblings, index) => [
        ...siblings.slice(0, index),
        ...cmsChangeMenuType(item, type, documents[0]?.slug),
        ...siblings.slice(index + 1),
      ]),
      'Type modifié sans supprimer les destinations.',
    );
  }
  async function remove(item: SiteCmsMenuItem) {
    if (
      (await ask({
        title: `Retirer « ${item.label} » du menu ?`,
        message: `Cette action retire ${1 + cmsMenuCount(item.children)} entrée(s) du brouillon, pas les pages ni les fichiers. Les rubriques devenues vides seront retirées.`,
        choices: [{ value: 'remove', label: 'Retirer du menu', danger: true }],
      })) !== 'remove'
    )
      return;
    commit(
      patch(items, cmsIdentity(item), (_, siblings, index) => siblings.filter((__, i) => i !== index)),
      'Entrée retirée. Une rubrique vide n’est pas conservée.',
    );
  }
  function render(nodes: SiteCmsMenuItem[], depth: number, parent?: SiteCmsMenuItem): ReactNode {
    return (
      <ul className='cms-tree'>
        {nodes.map((item, index) => (
          <li key={cmsIdentity(item)}>
            <div className={`cms-tree-row ${selected === cmsIdentity(item) ? 'is-selected' : ''}`}>
              <button
                type='button'
                className='cms-tree-select'
                aria-pressed={selected === cmsIdentity(item)}
                onClick={() => setSelected(cmsIdentity(item))}
              >
                <ChevronRight size={15} />
                <span>
                  {item.label || 'Sans libellé'}
                  <small>
                    {{ PAGE: 'Page', GROUP: 'Rubrique', DOCUMENT: 'PDF', EXTERNAL_LINK: 'Lien externe' }[item.type]} ·
                    niveau {depth}
                  </small>
                </span>
              </button>
              <div className='cms-tree-actions'>
                <button
                  type='button'
                  className='cms-icon-button'
                  aria-label={`Monter ${item.label}`}
                  disabled={index === 0}
                  onClick={() =>
                    commit(
                      patch(items, cmsIdentity(item), (_, siblings, at) => {
                        const next = [...siblings];
                        [next[at], next[at - 1]] = [next[at - 1], next[at]];
                        return next;
                      }),
                      'Lien déplacé.',
                    )
                  }
                >
                  <ArrowUp size={15} />
                </button>
                <button
                  type='button'
                  className='cms-icon-button'
                  aria-label={`Descendre ${item.label}`}
                  disabled={index === nodes.length - 1}
                  onClick={() =>
                    commit(
                      patch(items, cmsIdentity(item), (_, siblings, at) => {
                        const next = [...siblings];
                        [next[at], next[at + 1]] = [next[at + 1], next[at]];
                        return next;
                      }),
                      'Lien déplacé.',
                    )
                  }
                >
                  <ArrowDown size={15} />
                </button>
                <button
                  type='button'
                  className='cms-icon-button'
                  aria-label={`Imbriquer ${item.label}`}
                  disabled={!nodes[index - 1] || nodes[index - 1].type === 'EXTERNAL_LINK' || depth + height(item) > 3}
                  onClick={() =>
                    commit(
                      patch(items, cmsIdentity(item), (_, siblings, at) =>
                        siblings
                          .filter((__, i) => i !== at)
                          .map((entry, i) =>
                            i === at - 1
                              ? cmsKeepIdentity(entry, { ...entry, children: [...entry.children, item] })
                              : entry,
                          ),
                      ),
                      'Lien imbriqué.',
                    )
                  }
                >
                  <ArrowRight size={15} />
                </button>
                {parent && (
                  <button
                    type='button'
                    className='cms-icon-button'
                    aria-label={`Remonter ${item.label} d’un niveau`}
                    onClick={() =>
                      commit(
                        patch(items, cmsIdentity(parent), (p, siblings, at) => [
                          ...siblings.slice(0, at),
                          cmsKeepIdentity(p, {
                            ...p,
                            children: p.children.filter(c => cmsIdentity(c) !== cmsIdentity(item)),
                          }),
                          item,
                          ...siblings.slice(at + 1),
                        ]),
                        'Lien remonté.',
                      )
                    }
                  >
                    <ArrowLeft size={15} />
                  </button>
                )}
              </div>
            </div>
            {item.children.length ? render(item.children, depth + 1, item) : null}
          </li>
        ))}
      </ul>
    );
  }
  const publicMenu = cmsPublicContent(pages, items).headerMenu;
  function publicList(nodes: SiteCmsMenuItem[]): ReactNode {
    return (
      <ul>
        {nodes.map((n, i) => (
          <li key={`${n.label}-${i}`}>
            {n.label}
            {n.children.length ? publicList(n.children) : null}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className='cms-stack'>
      <div className='cms-card-heading'>
        <p className='cms-muted'>Sélectionnez une entrée pour modifier son libellé et sa destination.</p>
        <span className='cms-counter'>{count}/60 entrées</span>
      </div>
      {notice && (
        <p role='status' className='cms-hint'>
          {notice}
        </p>
      )}
      {undo && undo.revision === revision && (
        <button
          type='button'
          className='cms-link-button'
          onClick={() => {
            onChange(undo.items);
            setUndo(null);
            setNotice('Dernière opération de structure annulée.');
          }}
        >
          Annuler la dernière opération
        </button>
      )}
      <div className='cms-navigation-layout'>
        <div>
          {render(items, 1)}
          <div className='cms-row'>
            <button
              type='button'
              className='cms-button'
              disabled={count >= 60}
              onClick={() => {
                const item = home();
                commit([...items, item], 'Lien ajouté.');
                setSelected(cmsIdentity(item));
              }}
            >
              <Plus size={15} />
              Ajouter un lien
            </button>
            <button
              type='button'
              className='cms-button'
              disabled={count > 58}
              onClick={() => {
                const item: SiteCmsMenuItem = { type: 'GROUP', label: 'Rubrique', children: [home()] };
                commit([...items, item], 'Rubrique ajoutée avec un lien modifiable.');
                setSelected(cmsIdentity(item));
              }}
            >
              Ajouter une rubrique
            </button>
          </div>
        </div>
        <section className='cms-menu-inspector' aria-label='Propriétés du lien'>
          {current ? (
            <>
              <h4>Modifier « {current.label} »</h4>
              <label>
                Libellé
                <input
                  maxLength={90}
                  value={current.label}
                  onChange={e => edit(current, { ...current, label: e.target.value })}
                />
              </label>
              <label>
                Type
                <select
                  value={current.type}
                  onChange={e => void changeType(current, e.target.value as SiteCmsMenuItem['type'])}
                >
                  <option value='PAGE'>Page interne</option>
                  <option value='GROUP' disabled={findDepth(items, cmsIdentity(current)) === 3}>
                    Rubrique
                  </option>
                  <option value='DOCUMENT' disabled={!documents.length}>
                    Document PDF
                  </option>
                  <option value='EXTERNAL_LINK'>Lien externe</option>
                </select>
              </label>
              {current.type === 'PAGE' && (
                <label>
                  Page liée au menu
                  <select value={current.slug} onChange={e => edit(current, { ...current, slug: e.target.value })}>
                    {!pages.some(p => p.slug === current.slug) && (
                      <option value={current.slug}>Page indisponible : {current.slug}</option>
                    )}
                    {pages.map(p => (
                      <option key={p.slug} value={p.slug}>
                        {p.title}
                        {p.visibility === 'DRAFT' ? ' — privée' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {current.type === 'DOCUMENT' && (
                <label>
                  PDF lié au menu
                  <select value={current.slug} onChange={e => edit(current, { ...current, slug: e.target.value })}>
                    {!documents.some(d => d.slug === current.slug) && (
                      <option value={current.slug}>Document indisponible : {current.slug}</option>
                    )}
                    {documents.map(d => (
                      <option key={d.id} value={d.slug}>
                        {d.title}
                      </option>
                    ))}
                  </select>
                  <small>Ce document sera rendu public avec le menu.</small>
                </label>
              )}
              {current.type === 'EXTERNAL_LINK' && (
                <label>
                  Adresse HTTPS
                  <input
                    type='url'
                    value={current.url}
                    onChange={e => edit(current, { ...current, url: e.target.value })}
                  />
                </label>
              )}
              {current.type !== 'EXTERNAL_LINK' && findDepth(items, cmsIdentity(current)) < 3 && (
                <button
                  type='button'
                  className='cms-button'
                  disabled={count >= 60}
                  onClick={() => {
                    const child = home();
                    commit(
                      patch(items, cmsIdentity(current), (entry, siblings, at) =>
                        siblings.map((old, i) =>
                          i === at ? cmsKeepIdentity(entry, { ...entry, children: [...entry.children, child] }) : old,
                        ),
                      ),
                      'Sous-lien ajouté.',
                    );
                    setSelected(cmsIdentity(child));
                  }}
                >
                  Ajouter un sous-menu
                </button>
              )}
              <button type='button' className='cms-link-button cms-danger' onClick={() => void remove(current)}>
                <Trash2 size={15} />
                Retirer cette entrée
              </button>
            </>
          ) : (
            <p>Sélectionnez un lien à gauche. Les commandes de déplacement restent disponibles au clavier.</p>
          )}
        </section>
      </div>
      <details className='cms-public-menu'>
        <summary>Menu de la prochaine publication</summary>
        <p>Les parents privés sont retirés ; leurs descendants publics sont remontés sans exposer la page privée.</p>
        {publicList(publicMenu)}
      </details>
    </div>
  );
}
function findDepth(items: SiteCmsMenuItem[], id: string, depth = 1): number {
  for (const item of items) {
    if (cmsIdentity(item) === id) return depth;
    const nested = findDepth(item.children, id, depth + 1);
    if (nested) return nested;
  }
  return 0;
}
